import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, PenTool, User, Zap, Brain } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ErrorAlert from '../components/ErrorAlert';
import GoogleSignInButton from '../components/GoogleSignInButton';
import PasskeyLoginButton from '../components/PasskeyLoginButton';
import PasskeyEnrollPrompt from '../components/PasskeyEnrollPrompt';

const WriterLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ username: '', email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [enrollPrompt, setEnrollPrompt] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      console.log('Scribe Login - Attempting login for:', loginData.email);
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Enable cookies
        body: JSON.stringify(loginData)
      });

      console.log('Scribe Login - Response status:', res.status);

      if (!res.ok) {
        const errorData = await res.json();
        console.error('Scribe Login - Error:', errorData);
        setLoginError(errorData.detail || 'Writer login failed. Please check your credentials.');
        return;
      }

      const data = await res.json();
      console.log('Scribe Login - Success, role:', data.role);
      
      if (data.role !== 'writer') {
        setLoginError('Access denied. This account is not registered as a scribe.');
        return;
      }
      
      localStorage.setItem('userRole', 'writer');
      localStorage.setItem('userEmail', data.email);
      // Session token is now in httpOnly cookie
      setEnrollPrompt({ email: data.email || loginData.email, next: '/writer-dashboard' });
    } catch (error) {
      console.error('Scribe Login - Catch error:', error);
      setLoginError(`Network error: ${error.message}. Please check your connection and try again.`);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError('');

    // Client-side validation — mirror UserLogin.jsx so writer signup
    // surfaces the real issue instead of a generic catch-block message
    // (Feb 28 2026 user spec).
    const username = (registerData.username || '').trim();
    const email = (registerData.email || '').trim().toLowerCase();
    const password = registerData.password || '';
    if (!username) {
      setRegisterError('Please enter a pen name so readers can find you.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setRegisterError('Enter a valid email address.');
      return;
    }
    if (password.length < 8) {
      setRegisterError('Password must be at least 8 characters.');
      return;
    }

    try {
      const payload = { username, email, password, role: 'writer' };

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      // Robustly handle non-JSON responses (502/504 HTML pages while
      // the backend restarts in production).
      const rawText = await res.text();
      let parsed = null;
      try { parsed = rawText ? JSON.parse(rawText) : null; } catch { /* not JSON */ }

      if (!res.ok) {
        let detail = parsed?.detail;
        if (Array.isArray(detail)) {
          detail = detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
        }
        if (!detail) detail = `Writer registration failed (HTTP ${res.status}).`;
        if (typeof detail === 'string' && /already (registered|exists)/i.test(detail)) {
          setRegisterError(
            `${detail} If this is you, switch to the Login tab — or reset your password from there.`,
          );
        } else {
          setRegisterError(String(detail));
        }
        return;
      }

      const data = parsed || {};

      localStorage.setItem('userRole', 'writer');
      localStorage.setItem('userEmail', data.email || email);
      localStorage.setItem('username', data.name || username);
      setEnrollPrompt({ email: data.email || email, next: '/writer-dashboard' });
    } catch (error) {
      console.error('Writer Registration - Catch error:', error);
      setRegisterError(
        'Network error — please check your connection. If the problem persists, the server may be restarting; try again in a minute.',
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-slate-900 to-slate-800 flex items-center justify-center p-4">
      {enrollPrompt && (
        <PasskeyEnrollPrompt
          email={enrollPrompt.email}
          onClose={() => {
            const next = enrollPrompt.next;
            setEnrollPrompt(null);
            navigate(next);
          }}
        />
      )}
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-600/20 rounded-full mb-4">
            <PenTool className="w-10 h-10 text-purple-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Scribe Portal</h1>
          <p className="text-gray-400 text-sm">Access your content management tools</p>
        </div>

        {/* Pre-login teaser banners — let anonymous scribes sample the
            gamified UX (Speed Reader + Retention Quiz) before signup.
            Same XP-on-signup carrot pattern as the reader login. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          <Link
            to="/speed-test"
            className="group block rounded-2xl border border-[#FF3B30]/40 bg-gradient-to-r from-[#FF3B30]/15 to-[#FFD60A]/10 px-3 py-3 hover:border-[#FF3B30] transition"
            data-testid="prelogin-speed-test-link"
          >
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-[#FF3B30]/20 grid place-items-center shrink-0 group-hover:bg-[#FF3B30]/30 transition">
                <Zap className="w-4 h-4 text-[#FF3B30]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[9px] uppercase tracking-[0.25em] text-[#FFD60A] font-bold">Try free</p>
                <p className="text-xs font-bold text-white truncate">Speed Reader Challenge</p>
              </div>
            </div>
          </Link>
          <Link
            to="/quiz-demo"
            className="group block rounded-2xl border border-amber-400/40 bg-gradient-to-r from-amber-500/15 to-fuchsia-500/10 px-3 py-3 hover:border-amber-300 transition"
            data-testid="prelogin-quiz-demo-link"
          >
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-amber-500/20 grid place-items-center shrink-0 group-hover:bg-amber-500/30 transition">
                <Brain className="w-4 h-4 text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[9px] uppercase tracking-[0.25em] text-amber-300 font-bold">Try free</p>
                <p className="text-xs font-bold text-white truncate">Retention Quiz</p>
              </div>
            </div>
          </Link>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-purple-900/50">
          <div className="mb-5">
            <GoogleSignInButton role="pending_writer" label="Apply with Google" testId="writer-google-signin-btn" />
            <p className="text-[11px] text-gray-500 mt-2 text-center">
              New scribes go through admin review before unlocking writer tools.
            </p>
            <div className="mt-3">
              <PasskeyLoginButton
                defaultEmail={loginData.email}
                onSuccess={(u) => {
                  localStorage.setItem('userRole', u.role || 'user');
                  localStorage.setItem('userEmail', u.email);
                  const fallback = u.role === 'writer' ? '/writer-dashboard' : '/dashboard';
                  // First-login Speed Reader pop-up (one-shot per browser).
                  try {
                    if (!localStorage.getItem('speed_test_seen_v1')) {
                      localStorage.setItem('speed_test_seen_v1', String(Date.now()));
                      navigate('/speed-test?source=postlogin');
                      return;
                    }
                  } catch { /* fall through */ }
                  navigate(fallback);
                }}
                testId="writer-passkey-login-btn"
              />
            </div>
            <div className="flex items-center my-5">
              <div className="flex-1 h-px bg-slate-700" />
              <span className="px-3 text-xs text-gray-500 uppercase tracking-wider">or use email</span>
              <div className="flex-1 h-px bg-slate-700" />
            </div>
          </div>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="register">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <ErrorAlert message={loginError} onClose={() => setLoginError('')} />
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="Writer Email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    className="w-full pl-12 pr-4 py-6 bg-slate-900/50 border-purple-900/50 text-white"
                    required
                  />
                </div>
                
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    className="w-full pl-12 pr-12 py-6 bg-slate-900/50 border-purple-900/50 text-white"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                <Button
                  type="submit"
                  className="w-full py-6 bg-purple-600 hover:bg-purple-700"
                >
                  Access Scribe Portal
                </Button>
              </form>

              <div className="mt-4 text-center">
                <a 
                  href="/forgot-password" 
                  className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Forgot Password?
                </a>
              </div>
            </TabsContent>

            <TabsContent value="register">
              <ErrorAlert message={registerError} onClose={() => setRegisterError('')} />
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Username"
                    value={registerData.username}
                    onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                    className="w-full pl-12 pr-4 py-6 bg-slate-900/50 border-purple-900/50 text-white"
                    required
                  />
                </div>

                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                    className="w-full pl-12 pr-4 py-6 bg-slate-900/50 border-purple-900/50 text-white"
                    required
                  />
                </div>
                
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                    className="w-full pl-12 pr-12 py-6 bg-slate-900/50 border-purple-900/50 text-white"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                <Button
                  type="submit"
                  className="w-full py-6 bg-purple-600 hover:bg-purple-700"
                >
                  Register as Writer
                </Button>
              </form>

              <p className="text-xs text-gray-500 mt-4 text-center">
                By registering, you agree to our Terms of Service and can start uploading content.
              </p>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <a href="/login" className="text-sm text-gray-400 hover:text-white transition-colors">
              ← Back to Reader Login
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WriterLogin;